import { createConsumer } from '@rails/actioncable';
import { authService } from './authService';
import { toast } from './toastService';

class CableService {
  constructor() {
    this.consumer = null;
    this.subscriptions = new Map();
  }

  // Initialize ActionCable connection
  initializeConnection() {
    // Already connected — skip to avoid tearing down active subscriptions
    if (this.consumer) {
      console.log('⚡ initializeConnection() skipped — already connected');
      return this.consumer;
    }

    const token = authService.getToken();
    if (!token) {
      console.error('No auth token available for WebSocket connection');
      return null;
    }

    // Create consumer with authentication
    // Use specific backend URL for WebSocket as per instructions
    const backendUrl = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000').replace(/\/$/, '');
    const wsUrl = backendUrl.replace(/^https/, 'wss').replace(/^http/, 'ws') + '/cable';
    const connectionUrl = `${wsUrl}?token=${token}`;

    console.log('WebSocket URL:', wsUrl);
    console.log('Full connection URL:', connectionUrl);
    console.log('Token available:', !!token);

    this.consumer = createConsumer(connectionUrl);

    // ActionCable connection debugging - using proper event callbacks
    const connection = this.consumer.connection;
    if (connection && connection.monitor) {
      // Override monitor callbacks for debugging
      const originalConnected = connection.monitor.connected;
      const originalDisconnected = connection.monitor.disconnected;

      connection.monitor.connected = () => {
        console.log('✅ ActionCable WebSocket connected successfully');
        if (originalConnected) originalConnected.call(connection.monitor);
      };

      connection.monitor.disconnected = () => {
        console.log('❌ ActionCable WebSocket disconnected');
        if (originalDisconnected) originalDisconnected.call(connection.monitor);
      };
    }

    console.log('ActionCable connection initialized');

    // Auto-subscribe to NotificationsChannel on every new connection
    this._autoSubscribeNotifications();

    return this.consumer;
  }

  _autoSubscribeNotifications() {
    if (this._notificationsSubscription) {
      this._notificationsSubscription.unsubscribe();
      this._notificationsSubscription = null;
    }

    this._notificationsSubscription = this.consumer.subscriptions.create(
      { channel: 'NotificationsChannel' },
      {
        connected: () => {
          console.log('✅ Connected to NotificationsChannel');
        },
        disconnected: () => {
          console.log('❌ Disconnected from NotificationsChannel');
        },
        received: (data) => {
          console.log('🔔 Notification received:', data);
          try {
            if (data && data.title) {
              const icons = {
                job_match: '🔨', bid_received: '💰', quote_accepted: '✅',
                quote_rejected: '❌', message: '💬', booking_update: '📋',
                payment: '💳', system: '⚙️'
              };
              const colors = {
                job_match: 'bg-emerald-50 border-emerald-200 text-emerald-900',
                bid_received: 'bg-blue-50 border-blue-200 text-blue-900',
                quote_accepted: 'bg-green-50 border-green-200 text-green-900',
                quote_rejected: 'bg-amber-50 border-amber-200 text-amber-900',
                message: 'bg-sky-50 border-sky-200 text-sky-900',
                booking_update: 'bg-violet-50 border-violet-200 text-violet-900',
                payment: 'bg-teal-50 border-teal-200 text-teal-900',
                system: 'bg-slate-50 border-slate-200 text-slate-900',
              };

              const type = data.notification_type || 'system';

              // Resolve navigation URL: prefer explicit action_url, else derive from type + role
              const getNavUrl = () => {
                if (data.action_url) return data.action_url;
                try {
                  const user = JSON.parse(localStorage.getItem('current_user') || '{}');
                  const isProvider = user.role === 'provider' || user.role === 'service_provider';
                  const base = isProvider ? '/provider-dashboard' : '/customer-dashboard';
                  const tabMap = {
                    message:        `${base}?tab=messages`,
                    bid_received:   '/customer-dashboard?tab=bids',
                    quote_accepted: '/provider-dashboard?tab=bids',
                    quote_rejected: '/provider-dashboard?tab=bids',
                    job_match:      '/provider-dashboard?tab=projects',
                    booking_update: `${base}?tab=projects`,
                    payment:        `${base}?tab=subscription`,
                  };
                  return tabMap[type] || null;
                } catch { return null; }
              };

              const navUrl = getNavUrl();

              toast({
                title: `${icons[type] || '🔔'} ${data.title}`,
                description: navUrl ? `${data.body} — tap to view` : data.body,
                className: `${colors[type] || colors.system} border-2 ${navUrl ? 'cursor-pointer' : ''}`,
                duration: 6000,
                onClick: navUrl
                  ? () => window.dispatchEvent(new CustomEvent('notification:navigate', { detail: { url: navUrl, data } }))
                  : undefined,
              });
            }
          } catch (e) {
            console.warn('Toast not available:', e.message);
          }
          // Dispatch window event for bell badge/list updates
          window.dispatchEvent(new CustomEvent('notifications:new', { detail: { notification: data } }));
          window.dispatchEvent(new CustomEvent('notifications:unread_increment'));
        },
      }
    );
  }

  // Subscribe to a chat room channel
  subscribeToChatRoom(chatRoomId, callbacks = {}) {
    console.log(`🔌 Attempting to subscribe to chat room ${chatRoomId}`);

    if (!this.consumer) {
      console.log('No consumer found, initializing connection...');
      this.initializeConnection();
    }

    if (!this.consumer) {
      console.error('❌ Failed to initialize ActionCable connection');
      return null;
    }

    const subscriptionKey = `chat_room_${chatRoomId}`;

    // Unsubscribe if already subscribed
    if (this.subscriptions.has(subscriptionKey)) {
      console.log(
        `Unsubscribing from existing subscription for chat room ${chatRoomId}`,
      );
      this.unsubscribeFromChatRoom(chatRoomId);
    }

    console.log(`Creating subscription for chat room ${chatRoomId}...`);
    const subscription = this.consumer.subscriptions.create(
      {
        channel: 'ChatRoomChannel',
        chat_room_id: chatRoomId,
      },
      {
        connected: () => {
          console.log(`✅ Connected to chat room ${chatRoomId}`);
          if (callbacks.onConnected) {
            callbacks.onConnected();
          }
        },

        disconnected: () => {
          console.log(`❌ Disconnected from chat room ${chatRoomId}`);
          if (callbacks.onDisconnected) {
            callbacks.onDisconnected();
          }
        },

        received: (data) => {
          console.log(`📨 Received message in chat room ${chatRoomId}:`, data);
          if (callbacks.onMessageReceived) {
            callbacks.onMessageReceived(data);
          }
        },

        rejected: () => {
          console.error(
            `❌ Subscription to chat room ${chatRoomId} was rejected`,
          );
          if (callbacks.onRejected) {
            callbacks.onRejected();
          }
        },
      },
    );

    this.subscriptions.set(subscriptionKey, subscription);
    console.log(
      `✅ Subscription created for chat room ${chatRoomId}. Active subscriptions: ${this.subscriptions.size}`,
    );
    return subscription;
  }

  // Unsubscribe from a chat room channel
  unsubscribeFromChatRoom(chatRoomId) {
    const subscriptionKey = `chat_room_${chatRoomId}`;
    const subscription = this.subscriptions.get(subscriptionKey);

    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(subscriptionKey);
      console.log(`Unsubscribed from chat room ${chatRoomId}`);
    }
  }

  // Send a message through the WebSocket
  sendMessage(chatRoomId, content, messageType = 'text') {
    console.log(
      `📤 Sending message via WebSocket to chat room ${chatRoomId}:`,
      content,
    );

    const subscriptionKey = `chat_room_${chatRoomId}`;
    const subscription = this.subscriptions.get(subscriptionKey);

    if (subscription) {
      const messageData = {
        content: content,
        message_type: messageType,
      };
      console.log('Message data:', messageData);

      subscription.perform('send_message', messageData);
      console.log('✅ Message sent via WebSocket');
    } else {
      console.error(
        `❌ No subscription found for chat room ${chatRoomId}. Available subscriptions:`,
        Array.from(this.subscriptions.keys()),
      );
    }
  }

  // Mark messages as read through WebSocket
  markAsRead(chatRoomId) {
    const subscriptionKey = `chat_room_${chatRoomId}`;
    const subscription = this.subscriptions.get(subscriptionKey);

    if (subscription) {
      subscription.perform('mark_as_read', {});
    }
  }

  // Send typing indicator
  sendTypingIndicator(chatRoomId, isTyping) {
    const subscriptionKey = `chat_room_${chatRoomId}`;
    const subscription = this.subscriptions.get(subscriptionKey);

    if (subscription) {
      subscription.perform('typing', {
        typing: isTyping,
      });
    }
  }

  // Subscribe to ProviderLeadsChannel for real-time new lead notifications
  subscribeToProviderLeads(callbacks = {}) {
    if (!this.consumer) {
      this.initializeConnection();
    }

    if (!this.consumer) {
      console.error('Failed to initialize ActionCable connection');
      return null;
    }

    const subscriptionKey = 'provider_leads';

    if (this.subscriptions.has(subscriptionKey)) {
      this.unsubscribeFromProviderLeads();
    }

    const subscription = this.consumer.subscriptions.create(
      { channel: 'ProviderLeadsChannel' },
      {
        connected: () => {
          console.log('✅ Connected to ProviderLeadsChannel');
          if (callbacks.onConnected) callbacks.onConnected();
        },
        disconnected: () => {
          console.log('❌ Disconnected from ProviderLeadsChannel');
          if (callbacks.onDisconnected) callbacks.onDisconnected();
        },
        received: (data) => {
          console.log('📨 New lead received:', data);
          if (callbacks.onReceived) callbacks.onReceived(data);
        },
      },
    );

    this.subscriptions.set(subscriptionKey, subscription);
    return subscription;
  }

  // Unsubscribe from ProviderLeadsChannel
  unsubscribeFromProviderLeads() {
    const subscriptionKey = 'provider_leads';
    const subscription = this.subscriptions.get(subscriptionKey);

    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(subscriptionKey);
      console.log('Unsubscribed from ProviderLeadsChannel');
    }
  }

  // Get connection state
  getConnectionState() {
    if (!this.consumer) return 'disconnected';
    const isOpen = this.consumer.connection.isOpen();
    console.log(
      'WebSocket connection state:',
      isOpen ? 'connected' : 'disconnected',
    );
    return isOpen ? 'connected' : 'disconnected';
  }

  // Debug function to log current status
  debugStatus() {
    console.log('=== ActionCable Debug Status ===');
    console.log('Consumer exists:', !!this.consumer);
    console.log('Connection state:', this.getConnectionState());
    console.log('Active subscriptions:', this.subscriptions.size);
    console.log('Subscription keys:', Array.from(this.subscriptions.keys()));
    console.log('Token available:', !!authService.getToken());
    console.log('================================');
  }

  // Disconnect all subscriptions and consumer
  disconnect() {
    // Unsubscribe from all channels
    this.subscriptions.forEach((subscription, key) => {
      subscription.unsubscribe();
      console.log(`Unsubscribed from ${key}`);
    });

    this.subscriptions.clear();

    // Disconnect the consumer
    if (this.consumer) {
      this.consumer.disconnect();
      this.consumer = null;
      console.log('ActionCable connection closed');
    }
  }

  // Reconnect if disconnected
  reconnect() {
    console.log('Reconnecting ActionCable...');
    this.disconnect();
    return this.initializeConnection();
  }

  // Get active subscriptions count
  getActiveSubscriptionsCount() {
    return this.subscriptions.size;
  }

  // Check if subscribed to specific chat room
  isSubscribedToChatRoom(chatRoomId) {
    return this.subscriptions.has(`chat_room_${chatRoomId}`);
  }

  // Test function to send a test message (for debugging)
  testConnection(chatRoomId) {
    console.log('🧪 Testing ActionCable connection...');
    this.debugStatus();

    if (chatRoomId) {
      console.log('Attempting to send test message to chat room:', chatRoomId);
      this.sendMessage(
        chatRoomId,
        `Test message ${new Date().toLocaleTimeString()}`,
      );
    }

    return this.getConnectionState();
  }

  unsubscribeFromNotifications() {
    if (this._notificationsSubscription) {
      this._notificationsSubscription.unsubscribe();
      this._notificationsSubscription = null;
    }
  }

  // Force reconnect for debugging
  forceReconnect() {
    console.log('🔄 Force reconnecting ActionCable...');
    this.disconnect();
    setTimeout(() => {
      this.initializeConnection();
    }, 1000);
  }
}

// Export singleton instance
export const cableService = new CableService();

// Make it globally available for debugging (only in development)
if (process.env.NODE_ENV === 'development') {
  window.cableService = cableService;
}
